import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Play, Lock, ArrowLeft, Video, MessageSquare, Sparkles, Brain,
  CheckCircle, Clock, Target, Award, ChevronRight, Star, Pause, RotateCcw, Volume2, VolumeX
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const CLASSROOM_SLIDE_SECONDS = 16;

export default function InterviewPrep({ user }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionType, setQuestionType] = useState('behavioral');
  const [progress, setProgress] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showPractice, setShowPractice] = useState(false);
  const [aiQuestions, setAiQuestions] = useState([]);
  const [jobRole, setJobRole] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [playbackSeconds, setPlaybackSeconds] = useState(0);
  const [rotationInfo, setRotationInfo] = useState(null);
  const [voiceStatus, setVoiceStatus] = useState('checking');
  const [voiceName, setVoiceName] = useState('');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const narrationRunRef = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      loadQuestions(questionType);
    }
  }, [questionType, hasAccess]);

  const checkAccess = async () => {
    try {
      const res = await api.get('/purchases/check/interview_prep');
      setHasAccess(res.data.has_access);
      if (res.data.has_access) {
        loadVideos();
        loadProgress();
        loadQuestions('behavioral');
      }
    } catch (error) {
      console.error('Access check failed');
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async () => {
    try {
      const res = await api.get('/interview-prep/videos');
      setVideos(res.data.videos);
      setRotationInfo({
        month: res.data.rotation_month,
        note: res.data.rotation_note,
        tailoredTo: res.data.tailored_to
      });
    } catch (error) {
      console.error('Failed to load videos');
    }
  };

  const loadQuestions = async (type) => {
    try {
      const res = await api.get(`/interview-prep/questions/${type}`);
      setQuestions(res.data.questions);
    } catch (error) {
      console.error('Failed to load questions');
    }
  };

  const loadProgress = async () => {
    try {
      const res = await api.get('/interview-prep/progress');
      setProgress(res.data);
    } catch (error) {
      console.error('Failed to load progress');
    }
  };

  const trackVideoWatch = async (videoId) => {
    try {
      await api.post(`/interview-prep/track-progress?video_id=${videoId}&practice_time=10`);
      loadProgress();
    } catch (error) {
      console.error('Failed to track progress');
    }
  };

  const openVideoLesson = async (video) => {
    stopLessonVoice();
    setSelectedVideo(video);
    setPlaybackSeconds(0);
    setCurrentSlideIndex(0);
    startLessonVoice(video, true);
    await trackVideoWatch(video.id);
  };

  const closeVideoLesson = () => {
    stopLessonVoice();
    setSelectedVideo(null);
    setPlaybackSeconds(0);
    setCurrentSlideIndex(0);
    setIsVideoPlaying(false);
  };

  const formatSeconds = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getVideoProgress = () => {
    const lessonDuration = getLessonDurationSeconds(selectedVideo);
    if (!lessonDuration) return 0;
    return Math.min(100, Math.round((playbackSeconds / lessonDuration) * 100));
  };

  const getLessonSlides = (video) => {
    if (!video) return [];
    const chapters = video.chapters || [];
    const takeaways = video.takeaways || [];
    return [
      {
        eyebrow: video.level || 'Lesson',
        title: video.title,
        body: video.description,
        bullets: ['What this lesson covers', 'How to use it in a real interview', 'What to practice after watching']
      },
      ...chapters.map((chapter, index) => ({
        eyebrow: `Chapter ${index + 1}`,
        title: chapter,
        body: index === 0
          ? video.transcript
          : `Apply this section to your own interview story. Keep your answer specific, job-related, and easy for the interviewer to follow.`,
        bullets: takeaways.slice(0, 3)
      })),
      {
        eyebrow: 'Action Plan',
        title: 'Practice before the interview',
        body: 'Turn the lesson into one clear answer you can say out loud. Then practice it once naturally instead of memorizing every word.',
        bullets: takeaways.length ? takeaways : ['Write one example', 'Say it out loud', 'Cut anything that does not help the interviewer decide']
      }
    ];
  };

  const getCurrentSlide = () => {
    const slides = getLessonSlides(selectedVideo);
    return slides[Math.min(currentSlideIndex, Math.max(slides.length - 1, 0))] || null;
  };

  const getLessonDurationSeconds = (video) => {
    const slides = getLessonSlides(video);
    return slides.length * CLASSROOM_SLIDE_SECONDS;
  };

  const getSlideProgress = () => {
    const slides = getLessonSlides(selectedVideo);
    if (!slides.length) return 0;
    return Math.round(((currentSlideIndex + 1) / slides.length) * 100);
  };

  const goToSlide = (index, narrate = false) => {
    const slides = getLessonSlides(selectedVideo);
    const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
    stopLessonVoice();
    setCurrentSlideIndex(nextIndex);
    if (slides.length) {
      setPlaybackSeconds(CLASSROOM_SLIDE_SECONDS * nextIndex);
    }
    setIsVideoPlaying(false);
    setVoiceStatus((status) => status === 'unavailable' ? status : 'ready');
    if (narrate) {
      window.setTimeout(() => startLessonVoice(selectedVideo, true, nextIndex), 0);
    }
  };

  const getSlideScript = (slide, index, total) => {
    if (!slide) return '';
    const bullets = (slide.bullets || []).map((bullet) => `Key point. ${bullet}.`).join(' ');
    return `Slide ${index + 1} of ${total}. ${slide.eyebrow}. ${slide.title}. ${slide.body}. ${bullets}`;
  };

  const getPreferredVoice = () => {
    if (!('speechSynthesis' in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    return (
      voices.find((voice) => voice.lang?.startsWith('en') && /female|natural|zira|aria|jenny|samantha/i.test(voice.name)) ||
      voices.find((voice) => voice.lang?.startsWith('en')) ||
      voices[0] ||
      null
    );
  };

  const stopLessonVoice = () => {
    narrationRunRef.current += 1;
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const finishLessonCourse = (video, runId) => {
    if (runId !== narrationRunRef.current) return;
    const slides = getLessonSlides(video);
    setVoiceStatus('finished');
    setIsVideoPlaying(false);
    setPlaybackSeconds(getLessonDurationSeconds(video));
    setCurrentSlideIndex(Math.max(slides.length - 1, 0));
    trackVideoWatch(video.id);

    const currentCourseIndex = videos.findIndex((item) => item.id === video.id);
    const nextCourse = currentCourseIndex >= 0 ? videos[currentCourseIndex + 1] : null;
    if (nextCourse) {
      toast.success(`Finished ${video.title}. Starting next course.`);
      window.setTimeout(() => {
        if (runId !== narrationRunRef.current) return;
        setSelectedVideo(nextCourse);
        setPlaybackSeconds(0);
        setCurrentSlideIndex(0);
        startLessonVoice(nextCourse, true, 0);
        trackVideoWatch(nextCourse.id);
      }, 1200);
    } else {
      toast.success('Course playlist complete');
    }
  };

  const startLessonVoice = (video = selectedVideo, restart = false, slideIndex = currentSlideIndex) => {
    if (!video) return;
    const slides = getLessonSlides(video);
    const startIndex = Math.max(0, Math.min(slideIndex, Math.max(slides.length - 1, 0)));

    if (!('speechSynthesis' in window)) {
      setVoiceStatus('unavailable');
      setIsVideoPlaying(true);
      return;
    }

    if (!restart && window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setVoiceStatus('speaking');
      setIsVideoPlaying(true);
      return;
    }

    window.speechSynthesis.cancel();
    const runId = narrationRunRef.current + 1;
    narrationRunRef.current = runId;

    const speakSlide = (index) => {
      if (runId !== narrationRunRef.current) return;
      const slide = slides[index];
      if (!slide) {
        finishLessonCourse(video, runId);
        return;
      }

      setCurrentSlideIndex(index);
      setPlaybackSeconds(CLASSROOM_SLIDE_SECONDS * index);
      const utterance = new SpeechSynthesisUtterance(getSlideScript(slide, index, slides.length));
      const voice = getPreferredVoice();
      if (voice) {
        utterance.voice = voice;
        setVoiceName(voice.name);
      }
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.onstart = () => {
        if (runId !== narrationRunRef.current) return;
        setVoiceStatus('speaking');
        setIsVideoPlaying(true);
      };
      utterance.onend = () => {
        if (runId !== narrationRunRef.current) return;
        if (index < slides.length - 1) {
          speakSlide(index + 1);
        } else {
          finishLessonCourse(video, runId);
        }
      };
      utterance.onerror = () => {
        if (runId !== narrationRunRef.current) return;
        setVoiceStatus('unavailable');
        setIsVideoPlaying(false);
        toast.error('Voice playback is blocked or unavailable in this browser');
      };
      window.speechSynthesis.speak(utterance);
    };

    speakSlide(restart ? startIndex : currentSlideIndex);
    setIsVideoPlaying(true);
  };

  const pauseLessonVoice = () => {
    if ('speechSynthesis' in window && window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setVoiceStatus('paused');
    }
    setIsVideoPlaying(false);
  };

  const restartLessonVoice = () => {
    setPlaybackSeconds(0);
    setCurrentSlideIndex(0);
    startLessonVoice(selectedVideo, true);
  };

  const getAIQuestions = async () => {
    try {
      const res = await api.post('/interview-prep/ai-practice', {
        question_type: questionType,
        job_role: jobRole || 'professional',
        difficulty: 'medium'
      });
      setAiQuestions(res.data.questions);
      toast.success('AI questions generated!');
    } catch (error) {
      toast.error('Failed to generate AI questions');
    }
  };

  const submitAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error('Please write your answer first');
      return;
    }

    try {
      const res = await api.post('/interview-prep/ai-feedback', {
        question: selectedQuestion?.question || aiQuestions[0],
        answer: userAnswer,
        job_role: jobRole
      });
      setFeedback(res.data);
      
      // Track practice
      if (selectedQuestion) {
        await api.post(`/interview-prep/track-progress?question_id=${selectedQuestion.id}&practice_time=5`);
        loadProgress();
      }
    } catch (error) {
      toast.error('Failed to get feedback');
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateProgress = () => {
    if (!progress) return 0;
    const totalItems = videos.length + questions.length;
    const completedItems = (progress.videos_watched?.length || 0) + (progress.questions_practiced?.length || 0);
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  useEffect(() => {
    if (!isVideoPlaying || !selectedVideo) return;
    const timer = setInterval(() => {
      setPlaybackSeconds((current) => {
        const next = current + 1;
        const lessonDuration = getLessonDurationSeconds(selectedVideo);
        if (next >= lessonDuration) {
          setIsVideoPlaying(false);
          setCurrentSlideIndex(getLessonSlides(selectedVideo).length - 1);
          return lessonDuration;
        }
        const slides = getLessonSlides(selectedVideo);
        if (slides.length > 0 && voiceStatus === 'unavailable') {
          setCurrentSlideIndex(Math.min(slides.length - 1, Math.floor(next / CLASSROOM_SLIDE_SECONDS)));
          return next;
        }
        const currentSlideEnd = Math.min(lessonDuration, (currentSlideIndex + 1) * CLASSROOM_SLIDE_SECONDS);
        return Math.min(next, currentSlideEnd);
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVideoPlaying, selectedVideo, currentSlideIndex, voiceStatus]);

  const activeSlide = getCurrentSlide();
  const lessonSlides = getLessonSlides(selectedVideo);

  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setVoiceStatus('unavailable');
      return;
    }

    const loadVoice = () => {
      const voice = getPreferredVoice();
      if (voice) {
        setVoiceName(voice.name);
        setVoiceStatus((status) => status === 'checking' ? 'ready' : status);
      } else {
        setVoiceStatus('ready');
      }
    };

    loadVoice();
    window.speechSynthesis.onvoiceschanged = loadVoice;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      narrationRunRef.current += 1;
      window.speechSynthesis.cancel();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle>Interview Prep</CardTitle>
            <CardDescription>Subscribe to access video lessons, practice questions, and AI-powered interview tools</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-2xl font-bold">$49 <span className="text-sm font-normal">initial</span></p>
              <p className="text-sm text-muted-foreground">+ $5/month for full access</p>
            </div>
            <Button onClick={() => navigate('/marketplace')} className="w-full bg-green-600 hover:bg-green-700" data-testid="go-to-marketplace">
              Subscribe Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-white/20" data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <h1 className="text-2xl font-heading font-bold">Interview Prep</h1>
          </div>
          
          {/* Progress Bar */}
          <div className="bg-white/20 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm">Your Progress</span>
              <span className="font-bold">{calculateProgress()}%</span>
            </div>
            <Progress value={calculateProgress()} className="h-2 bg-white/30" />
            <div className="flex gap-4 mt-3 text-xs">
              <span><Video size={12} className="inline mr-1" /> {progress?.videos_watched?.length || 0}/{videos.length} videos</span>
              <span><MessageSquare size={12} className="inline mr-1" /> {progress?.questions_practiced?.length || 0} questions practiced</span>
              <span><Clock size={12} className="inline mr-1" /> {progress?.total_practice_time || 0} min practice</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <Tabs defaultValue="videos" className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-md mb-8">
            <TabsTrigger value="videos" className="flex items-center gap-2">
              <Video size={16} /> Videos
            </TabsTrigger>
            <TabsTrigger value="practice" className="flex items-center gap-2">
              <MessageSquare size={16} /> Practice
            </TabsTrigger>
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Sparkles size={16} /> AI Tools
            </TabsTrigger>
          </TabsList>

          {/* Video Lessons */}
          <TabsContent value="videos">
            {rotationInfo && (
              <div className="mb-6 rounded border border-green-200 bg-green-50 p-4 text-green-950">
                <div className="flex items-center gap-2 font-semibold">
                  <Sparkles size={18} />
                  Monthly AI-Curated Rotation: {rotationInfo.month}
                </div>
                <p className="mt-1 text-sm">{rotationInfo.note}</p>
                {rotationInfo.tailoredTo && (
                  <p className="mt-1 text-xs">Tailored to: {rotationInfo.tailoredTo}</p>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videos.map((video) => {
                const watched = progress?.videos_watched?.includes(video.id);
                return (
                  <Card key={video.id} className={`overflow-hidden hover:shadow-lg transition-shadow ${watched ? 'border-green-500' : ''}`} data-testid={`video-${video.id}`}>
                    <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-emerald-950 aspect-video flex items-center justify-center">
                      <div className="absolute inset-0 p-5 text-white">
                        <div className="text-xs uppercase tracking-wide text-emerald-200">{video.level || 'Lesson'}</div>
                        <div className="mt-3 max-w-[80%] text-xl font-semibold leading-tight">{video.title}</div>
                        <div className="mt-3 text-xs text-slate-300">{video.description}</div>
                      </div>
                      <Button 
                        size="lg" 
                        className="relative rounded-full w-16 h-16 bg-white text-slate-950 hover:bg-emerald-50"
                        onClick={() => openVideoLesson(video)}
                      >
                        <Play size={24} fill="white" />
                      </Button>
                      <Badge className="absolute top-2 right-2 bg-black/50">{video.duration}</Badge>
                      {video.is_monthly_ai && (
                        <Badge className="absolute bottom-2 right-2 bg-emerald-500 text-white">AI Monthly</Badge>
                      )}
                      {watched && (
                        <div className="absolute top-2 left-2 bg-green-500 rounded-full p-1">
                          <CheckCircle size={14} className="text-white" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1">{video.title}</h3>
                      <p className="mb-3 text-sm text-muted-foreground line-clamp-2">{video.description}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">{video.category.replace('_', ' ')}</Badge>
                        <Badge variant="secondary" className="text-xs">{video.level || 'Core'}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Practice Questions */}
          <TabsContent value="practice">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Question Type Selection */}
              <div className="space-y-4">
                <h3 className="font-semibold mb-2">Question Type</h3>
                {['behavioral', 'technical', 'situational'].map((type) => (
                  <Card 
                    key={type}
                    className={`cursor-pointer transition-all ${questionType === type ? 'border-green-500 bg-green-50' : 'hover:border-gray-400'}`}
                    onClick={() => setQuestionType(type)}
                    data-testid={`type-${type}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {type === 'behavioral' && <Brain className="text-purple-500" />}
                        {type === 'technical' && <Target className="text-blue-500" />}
                        {type === 'situational' && <MessageSquare className="text-orange-500" />}
                        <span className="capitalize font-medium">{type}</span>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground" />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Questions List */}
              <div className="lg:col-span-2 space-y-4">
                <h3 className="font-semibold">Practice Questions</h3>
                {questions.map((q) => {
                  const practiced = progress?.questions_practiced?.includes(q.id);
                  return (
                    <Card 
                      key={q.id} 
                      className={`cursor-pointer hover:shadow-md transition-all ${practiced ? 'border-l-4 border-l-green-500' : ''}`}
                      onClick={() => {
                        setSelectedQuestion(q);
                        setShowPractice(true);
                        setUserAnswer('');
                        setFeedback(null);
                      }}
                      data-testid={`question-${q.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <p className="text-sm">{q.question}</p>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={getDifficultyColor(q.difficulty)}>{q.difficulty}</Badge>
                            {practiced && <CheckCircle size={16} className="text-green-500" />}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* AI Tools */}
          <TabsContent value="ai">
            <div className="grid md:grid-cols-2 gap-8">
              {/* AI Question Generator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="text-purple-500" /> AI Question Generator
                  </CardTitle>
                  <CardDescription>Get personalized interview questions for your target role</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Target Job Role</Label>
                    <Input 
                      value={jobRole} 
                      onChange={(e) => setJobRole(e.target.value)}
                      placeholder="e.g., Software Engineer, Product Manager"
                      data-testid="input-job-role"
                    />
                  </div>
                  <div>
                    <Label>Question Type</Label>
                    <div className="flex gap-2 mt-2">
                      {['behavioral', 'technical', 'situational'].map((type) => (
                        <Button 
                          key={type}
                          variant={questionType === type ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setQuestionType(type)}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={getAIQuestions} className="w-full" data-testid="generate-questions-btn">
                    <Sparkles size={16} className="mr-2" /> Generate Questions
                  </Button>
                  
                  {aiQuestions.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {aiQuestions.map((q, index) => (
                        <div key={index} className="bg-purple-50 rounded-lg p-3 text-sm">
                          <p>{q}</p>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="mt-2"
                            onClick={() => {
                              setSelectedQuestion({ question: q, id: `ai-${index}` });
                              setShowPractice(true);
                              setUserAnswer('');
                              setFeedback(null);
                            }}
                          >
                            Practice This
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Answer Coach */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="text-blue-500" /> AI Answer Coach
                  </CardTitle>
                  <CardDescription>Get instant feedback on your interview answers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Your Answer</Label>
                    <Textarea 
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      placeholder="Type your answer to any interview question..."
                      rows={6}
                      data-testid="input-answer"
                    />
                  </div>
                  <Button onClick={submitAnswer} className="w-full" data-testid="get-feedback-btn">
                    <Award size={16} className="mr-2" /> Get Feedback
                  </Button>
                  
                  {feedback && (
                    <div className="space-y-4 mt-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-green-600">{feedback.score}/100</div>
                        <p className="text-sm text-muted-foreground">Answer Score</p>
                      </div>
                      
                      <div className="space-y-2">
                        {feedback.feedback.map((f, index) => (
                          <p key={index} className="text-sm flex items-start gap-2">
                            <Star size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                            {f}
                          </p>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 rounded-lg p-3">
                        <h4 className="font-medium text-sm mb-1">💡 Tip</h4>
                        <p className="text-xs text-muted-foreground">{feedback.example_improvement}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Practice Dialog */}
      <Dialog open={showPractice} onOpenChange={setShowPractice}>
        <DialogContent className="max-w-2xl" data-testid="practice-dialog">
          <DialogHeader>
            <DialogTitle>Practice Question</DialogTitle>
            <DialogDescription>{selectedQuestion?.question}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Your Answer</Label>
              <Textarea 
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
                placeholder="Type your answer here... Try to use the STAR method (Situation, Task, Action, Result)"
                rows={8}
                data-testid="practice-answer-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={submitAnswer} className="flex-1" data-testid="submit-practice-btn">
                <Award size={16} className="mr-2" /> Get AI Feedback
              </Button>
            </div>
            
            {feedback && (
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-green-600">{feedback.score}</div>
                  <div className="flex-1">
                    <Progress value={feedback.score} className="h-2" />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-medium text-green-600 mb-1">✓ Strengths</h4>
                    <ul className="space-y-1">
                      {feedback.strengths.filter(Boolean).map((s, i) => (
                        <li key={i} className="text-muted-foreground">• {s}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium text-orange-600 mb-1">↑ Improvements</h4>
                    <ul className="space-y-1">
                      {feedback.improvements.filter(Boolean).map((s, i) => (
                        <li key={i} className="text-muted-foreground">• {s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedVideo} onOpenChange={(open) => !open && closeVideoLesson()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="video-lesson-dialog">
          {selectedVideo && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedVideo.title}</DialogTitle>
                <DialogDescription>{selectedVideo.description}</DialogDescription>
              </DialogHeader>

              <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
                <div className="space-y-4">
                  <div className="relative aspect-video overflow-hidden rounded bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 text-white">
                    <div className="absolute inset-0 flex flex-col justify-between p-6">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className="bg-emerald-500">{activeSlide?.eyebrow || selectedVideo.level || 'Lesson'}</Badge>
                          <Badge className={voiceStatus === 'unavailable' ? 'bg-red-500' : 'bg-blue-500'}>
                            {voiceStatus === 'unavailable' ? <VolumeX size={12} className="mr-1" /> : <Volume2 size={12} className="mr-1" />}
                            Voice {voiceStatus === 'speaking' ? 'On' : voiceStatus === 'paused' ? 'Paused' : voiceStatus === 'unavailable' ? 'Unavailable' : 'Ready'}
                          </Badge>
                        </div>
                        <h3 className="mt-4 max-w-xl text-3xl font-bold leading-tight">{activeSlide?.title}</h3>
                        <p className="mt-3 max-w-xl text-sm text-slate-300">{activeSlide?.body}</p>
                        <div className="mt-4 grid gap-2 sm:grid-cols-3">
                          {(activeSlide?.bullets || []).slice(0, 3).map((bullet) => (
                            <div key={bullet} className="rounded bg-white/10 p-2 text-xs text-slate-100">
                              {bullet}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Progress value={getVideoProgress()} className="h-2 bg-white/20" />
                        <div className="mt-2 flex justify-between text-xs text-slate-300">
                          <span>{formatSeconds(playbackSeconds)}</span>
                          <span>Slide {currentSlideIndex + 1}/{lessonSlides.length} • classroom pace {formatSeconds(getLessonDurationSeconds(selectedVideo))}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => isVideoPlaying ? pauseLessonVoice() : startLessonVoice(selectedVideo)} data-testid="lesson-play-pause-btn">
                      {isVideoPlaying ? <Pause size={16} className="mr-2" /> : <Play size={16} className="mr-2" />}
                      {isVideoPlaying ? 'Pause Voice' : 'Play Voice'}
                    </Button>
                    <Button variant="outline" onClick={restartLessonVoice}>
                      <RotateCcw size={16} className="mr-2" />
                      Restart
                    </Button>
                    <Button variant="outline" onClick={() => trackVideoWatch(selectedVideo.id)}>
                      <CheckCircle size={16} className="mr-2" />
                      Mark Complete
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {voiceStatus === 'unavailable'
                      ? 'Voice playback is not available in this browser. Try Edge or Chrome with sound enabled.'
                      : `Voice playback uses your browser voice${voiceName ? `: ${voiceName}` : ''}.`}
                  </p>
                  <div className="rounded border bg-white p-3">
                    <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Course slide progress</span>
                      <span>{getSlideProgress()}%</span>
                    </div>
                    <Progress value={getSlideProgress()} className="h-2" />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => goToSlide(currentSlideIndex - 1, isVideoPlaying)} disabled={currentSlideIndex === 0}>
                        Previous Slide
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => goToSlide(currentSlideIndex + 1, isVideoPlaying)} disabled={currentSlideIndex >= lessonSlides.length - 1}>
                        Next Slide
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Course Slides</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {lessonSlides.map((slide, index) => (
                        <button
                          key={`${slide.eyebrow}-${slide.title}`}
                          type="button"
                          onClick={() => goToSlide(index, isVideoPlaying)}
                          className={`flex w-full items-center gap-3 rounded border p-2 text-left text-sm ${index === currentSlideIndex ? 'border-emerald-500 bg-emerald-50' : 'bg-white hover:bg-slate-50'}`}
                        >
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-emerald-100 text-xs font-semibold text-emerald-700">{index + 1}</span>
                          <span>
                            <span className="block text-xs text-muted-foreground">{slide.eyebrow}</span>
                            <span className="font-medium">{slide.title}</span>
                          </span>
                        </button>
                      ))}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Best Takeaways</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(selectedVideo.takeaways || []).map((takeaway) => (
                        <p key={takeaway} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={15} className="mt-0.5 text-green-600" />
                          {takeaway}
                        </p>
                      ))}
                    </CardContent>
                  </Card>

                  {selectedVideo.is_monthly_ai && (
                    <Card className="border-emerald-200 bg-emerald-50">
                      <CardContent className="p-4 text-sm text-emerald-950">
                        <p className="font-semibold">Monthly AI Rotation</p>
                        <p className="mt-1">This lesson is part of {selectedVideo.rotation_month}. The platform refreshes this section each month with new AI-curated interview topics.</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
